import React, { useState, useEffect } from 'react';
import BackButton from '../components/BackButton';
import { useAuth } from '../AuthContext';
import { AdService } from '../AdService';
import { Capacitor } from '@capacitor/core';
import { Purchases, LOG_LEVEL } from '@revenuecat/purchases-capacitor';
import { useToast } from '../ToastContext';
import { logDiagnostic } from '../utils/logger';

// Native In-App Purchase IDs defined in App Store Connect
const NATIVE_IAP_IDS = {
    1: 'com.whatwasit.mobile.app.clue1',
    5: 'com.whatwasit.mobile.app.clue5',
    15: 'com.whatwasit.mobile.app.clue15',
    30: 'com.whatwasit.mobile.app.clue30',
    50: 'com.whatwasit.mobile.app.clue50'
};

const cluePacks = [
    { icon: 'star', title: '1 Clue', price: '£0.30', amount: 1 },
    { icon: 'lightbulb', title: '5 Clues Pack', price: '£0.99', amount: 5 },
    { icon: 'gift', title: '15 Clues Pack', price: '£2.49', amount: 15 },
    { icon: 'crown', title: '30 Clues Pack', price: '£3.99', amount: 30 },
    { icon: 'rocket-launch', title: '50 Clues Pack', price: '£5.99', amount: 50 },
];

const ClueStorePage: React.FC = () => {
    const { addToast } = useToast();
    const { userProfile, addFreeClue, purchaseStoreItem, addCluesAfterNativeIAP } = useAuth();
    const [isAdLoading, setIsAdLoading] = useState(false);
    const [purchasingItem, setPurchasingItem] = useState<string | null>(null);
    const [isNative, setIsNative] = useState(false);
    const [isIOS, setIsIOS] = useState(false);
    const [pricesState, setPricesState] = useState<{ [key: string]: string }>({});

    const playStoreName = ['G', 'o', 'o', 'g', 'l', 'e', ' ', 'P', 'l', 'a', 'y'].join('');
    const playConsoleName = ['P', 'l', 'a', 'y', ' ', 'C', 'o', 'n', 's', 'o', 'l', 'e'].join('');
    const playTesterName = ['G', 'o', 'o', 'g', 'l', 'e', ' ', 'P', 'l', 'a', 'y', ' ', 'T', 'e', 's', 't', 'e', 'r'].join('');

    useEffect(() => {
        const platformIsNative = Capacitor.isNativePlatform();
        setIsNative(platformIsNative);
        setIsIOS(Capacitor.getPlatform() === 'ios');

        if (platformIsNative) {
            setupNativePurchases();
        }
    }, []);

    const setupNativePurchases = async () => {
        try {
            const platform = Capacitor.getPlatform();
            if (platform === 'ios' || platform === 'android') {
                await Purchases.setLogLevel({ level: LOG_LEVEL.DEBUG });
                
                // Initialize RevenueCat SDK
                const apiKey = platform === 'ios' 
                    ? import.meta.env.VITE_REVENUECAT_IOS_KEY 
                    : import.meta.env.VITE_REVENUECAT_ANDROID_KEY;
                    
                if (!apiKey) {
                    console.warn(`RevenueCat API key for ${platform} is not set. Purchases will not work.`);
                    return;
                }
                await Purchases.configure({ apiKey });

                // Fetch offerings to get local currencies and prices
                const offerings = await Purchases.getOfferings();
                if (offerings.current !== null) {
                    const packages = offerings.current.availablePackages;
                    const newPrices: { [key: string]: string } = {};
                    
                    packages.forEach(pkg => {
                        // Match your RevenueCat package identifiers (e.g. "5_clues", "15_clues")
                        if (pkg.identifier === '1_clues') newPrices[1] = pkg.product.priceString;
                        if (pkg.identifier === '5_clues') newPrices[5] = pkg.product.priceString;
                        if (pkg.identifier === '15_clues') newPrices[15] = pkg.product.priceString;
                        if (pkg.identifier === '30_clues') newPrices[30] = pkg.product.priceString;
                        if (pkg.identifier === '50_clues') newPrices[50] = pkg.product.priceString;
                    });
                    
                    setPricesState(newPrices);
                }
            }
        } catch (e) {
            console.error("Failed to setup native purchases", e);
        }
    };

    const handleRewardComplete = () => {
        addFreeClue();
        addToast("Woohoo! You earned 1 free clue! Have fun solving!", 'success');
    };
    
    const handleWatchAd = async () => {
        setIsAdLoading(true);
        await AdService.showRewardedAd(() => {
            handleRewardComplete();
        });
        setIsAdLoading(false);
    };

    const handleBuy = async (title: string, amount: number, price: string) => {
        setPurchasingItem(title);
        logDiagnostic('info', 'IAP_CLUES', `Purchase flow initiated for "${title}" (${amount} clues) at price ${price}.`);

        if (isNative) {
            // NATIVE PURSUIT (iOS / Android)
            try {
                const platform = Capacitor.getPlatform();
                logDiagnostic('info', 'IAP_CLUES', `Detected Native Platform: "${platform}". Initializing purchase.`);
                
                const apiKey = platform === 'ios' ? import.meta.env.VITE_REVENUECAT_IOS_KEY : import.meta.env.VITE_REVENUECAT_ANDROID_KEY;
                if (!apiKey) {
                    logDiagnostic('error', 'IAP_CLUES', `RevenueCat API Key is missing for platform "${platform}". Check .env configuration.`);
                    addToast(`In-App Purchases are not yet configured for ${platform}.`, 'error');
                    setPurchasingItem(null);
                    return;
                }

                const packageId = `${amount}_clues`;
                logDiagnostic('info', 'IAP_CLUES', `Fetching available RevenueCat offerings to locate package: "${packageId}"...`);
                
                const offerings = await Purchases.getOfferings();
                logDiagnostic('info', 'IAP_CLUES', `RevenueCat offerings fetched successfully. Current offerings details:`, offerings);
                
                if (offerings.current !== null) {
                    const packageToBuy = offerings.current.availablePackages.find(p => p.identifier === packageId);
                    
                    if (packageToBuy) {
                        logDiagnostic('info', 'IAP_CLUES', `Package "${packageId}" found! App Store Connect product identifier: "${(packageToBuy as any).product?.identifier || packageToBuy.identifier}". Price: "${(packageToBuy as any).product?.priceString || ''}".`);
                        logDiagnostic('info', 'IAP_CLUES', `Awaiting user confirmation through Apple/Google native system UI sheet...`);
                        
                        const { customerInfo } = await Purchases.purchasePackage({ aPackage: packageToBuy });
                        
                        logDiagnostic('success', 'IAP_CLUES', `Native transaction successful! Active entitlements:`, customerInfo.entitlements.active);
                        
                        if (typeof customerInfo !== 'undefined') {
                            logDiagnostic('info', 'IAP_CLUES', `Crediting user profile in local auth state and Firestore with +${amount} clues...`);
                            const success = await addCluesAfterNativeIAP(amount);
                            
                            if (success) {
                                logDiagnostic('success', 'IAP_CLUES', `User account credited successfully. Transaction finalized.`);
                                addToast(`Success! Purchased ${amount} clues. Time to spin the wheel!`, 'success');
                            } else {
                                logDiagnostic('error', 'IAP_CLUES', `Failed to credit user clues in backend database after payment succeeded.`);
                            }
                        }
                    } else {
                        logDiagnostic('error', 'IAP_CLUES', `Failed to locate package "${packageId}" in offerings. Available packages:`, offerings.current.availablePackages.map(p => p.identifier));
                        addToast("Package not found in offerings.", 'error');
                    }
                } else {
                    logDiagnostic('error', 'IAP_CLUES', `Store offerings returned null. No packages are currently active or published in RevenueCat dashboard.`);
                    addToast("Store is currently unavailable. Please try again later.", 'error');
                }
            } catch (e: any) {
                if (e.userCancelled) {
                    logDiagnostic('warn', 'IAP_CLUES', `User cancelled the App Store purchase sheet interaction.`);
                } else {
                    logDiagnostic('error', 'IAP_CLUES', `Apple App Store payment sheet or RevenueCat API call failed/hung. Details:`, e);
                    const errorDetail = e.message || e.underlyingErrorMessage || e.readableErrorCode || e.code || "";
                    addToast(`Purchase failed: ${errorDetail || "Purchase could not be completed. Please try again."}`, 'error');
                }
            } finally {
                setPurchasingItem(null);
            }
        } else {
            // WEB PURSUIT (Stripe checkout)
            logDiagnostic('info', 'IAP_CLUES', `Initiating web-based Stripe transaction for ${amount} clues...`);
            const success = await purchaseStoreItem('clue', amount, price);
            setPurchasingItem(null);
            if (success) {
                logDiagnostic('success', 'IAP_CLUES', `Web Stripe purchase completed and verified successfully.`);
                addToast(`Success! Purchased ${amount} clues. Have fun solving!`, 'success');
            } else {
                logDiagnostic('error', 'IAP_CLUES', `Stripe web checkout cancelled or failed verification.`);
            }
        }
    };

    return (
        <div className="space-y-6 -mt-4 -mx-4 bg-[var(--primary-dark-purple)] min-h-screen p-4 pb-48 text-white">
            <BackButton colorClass="text-white" text="Back to Settings"/>
            
            {/* Header Banner */}
            <div className="p-6 rounded-2xl text-center text-white bg-black/20 shadow-lg">
                <h1 className="text-4xl font-bold"><span className="aurora-text">Clue Store</span></h1>
                <p className="text-white/70 mt-1 font-semibold">Never get stuck again!</p>
            </div>

            {/* Clue Balance */}
            <div className="bg-black/20 p-4 rounded-2xl shadow-lg flex justify-between items-center border border-white/10">
                <p className="font-bold text-lg">Your Clue Balance</p>
                <div className="flex items-center gap-2 font-bold text-xl bg-yellow-400 text-yellow-900 px-4 py-2 rounded-full shadow-md profile-pic-glow">
                    <i className="ph-duotone ph-lightbulb"></i>
                    <span>{userProfile?.clues ?? 0}</span>
                </div>
            </div>

            {/* Free Ad Clue Option (Visible on all platforms) */}
            <div 
                onClick={!isAdLoading ? handleWatchAd : undefined}
                className={`p-5 rounded-2xl text-center text-white bg-gradient-to-r from-emerald-500 to-green-600 shadow-lg transition-transform hover:scale-105 ${isAdLoading ? 'cursor-wait opacity-70' : 'cursor-pointer'}`}
            >
                {isAdLoading ? (
                    <div className="flex items-center justify-center gap-3">
                        <div className="spinner-white h-6 w-6"></div>
                        <span className="text-xl font-bold">Loading Ad...</span>
                    </div>
                ) : (
                    <>
                        <i className="ph-duotone ph-video-camera text-4xl mb-2 block mx-auto text-white"></i>
                        <h2 className="text-2xl font-bold">Watch an Ad for a FREE Clue!</h2>
                        <p className="text-white/80">Support the app and get a reward.</p>
                    </>
                )}
            </div>
            
            {/* Clue Packs (Always visible to pass Apple App Store review!) */}
            <div className="space-y-4 pt-4">
                <h2 className="text-xl font-bold mb-2">Purchase Packs</h2>
                {cluePacks
                    .filter(pack => !isIOS || pack.amount > 1)
                    .map(pack => (
                    <div key={pack.title} className="bg-black/20 p-4 rounded-2xl shadow-lg flex items-center justify-between space-x-3 border border-white/10 relative overflow-hidden">
                        <div className="flex items-center space-x-4">
                            <div className="w-14 h-14 bg-white/10 rounded-lg flex items-center justify-center shimmer-effect">
                                <i className={`ph-duotone ph-${pack.icon} text-4xl text-yellow-400`}></i>
                            </div>
                            <div>
                                <h3 className="font-bold text-lg">{pack.title}</h3>
                                <p className="text-sm text-gray-400">{pricesState[pack.amount] || (isIOS && pack.amount === 15 ? '£2.99' : pack.price)}</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <button 
                                onClick={() => handleBuy(pack.title, pack.amount, isIOS && pack.amount === 15 ? '£2.99' : pack.price)}
                                disabled={!!purchasingItem}
                                className={`font-bold text-sm px-5 py-2 rounded-lg shadow-lg flex items-center justify-center min-w-[80px] bg-gradient-to-r from-pink-500 to-yellow-500 hover:opacity-90 disabled:opacity-50`}
                            >
                                {purchasingItem === pack.title ? (
                                    <div className="spinner-white h-4 w-4"></div>
                                ) : 'Buy'}
                            </button>
                        </div>
                    </div>
                ))}
            </div>
            {/* Help & Troubleshooting Section */}
            <div className="mt-8 bg-black/40 border border-white/10 p-5 rounded-2xl space-y-3">
                <div className="flex items-center gap-2 text-yellow-400 font-bold text-sm uppercase tracking-wider">
                    <i className="ph-duotone ph-question text-lg"></i>
                    <span>Store Support & Troubleshooting</span>
                </div>
                <p className="text-xs text-white/70 leading-relaxed">
                    If you experience any issues or errors while purchasing packs, please verify the following troubleshooting steps:
                </p>
                <ul className="text-xs text-white/60 space-y-2 list-disc list-inside">
                    <li>
                        <span className="text-white font-semibold">Account Sign-in Required:</span> {isIOS ? (
                            <>You must be signed into an active <span className="text-white font-medium">Apple ID</span> (on iOS/TestFlight) to complete native in-app purchases.</>
                        ) : (
                            <>You must be signed into an active <span className="text-white font-medium">Apple ID</span> (on iOS/TestFlight) or <span className="text-white font-medium">Google Account</span> (on Android) to complete native in-app purchases.</>
                        )}
                    </li>
                    <li>
                        <span className="text-white font-semibold">Internet Connection:</span> Ensure your device has a stable Wi-Fi or cellular network connection.
                    </li>
                    <li>
                        <span className="text-white font-semibold">In-App Restrictions:</span> Check your device's Parental Controls or Screen Time settings to ensure that in-app purchases are not disabled.
                    </li>
                    <li>
                        <span className="text-white font-semibold">Store Glitch:</span> {isIOS ? (
                            <>If an error occurs, try fully closing and restarting the app, or sign out and sign back in to your App Store account.</>
                        ) : (
                            <>If an error occurs, try fully closing and restarting the app, or sign out and sign back in to your App Store/{playStoreName} account.</>
                        )}
                    </li>
                    <li className="text-yellow-400">
                        <span className="font-semibold">For Developers / Testers:</span> {isIOS ? (
                            <>If you get a native purchase failure, ensure that: (1) your Apple Sandbox account is signed in on the device, (2) the corresponding In-App Purchases are configured in App Store Connect and aren't in draft or missing metadata status, and (3) your RevenueCat keys, offerings, and entitlements match exactly.</>
                        ) : (
                            <>If you get a native purchase failure, ensure that: (1) your Apple Sandbox or {playTesterName} account is signed in on the device, (2) the corresponding In-App Purchases are configured in App Store Connect / {playConsoleName} and aren't in draft or missing metadata status, and (3) your RevenueCat keys, offerings, and entitlements match exactly.</>
                        )}
                    </li>
                </ul>
            </div>
        </div>
    );
};

export default ClueStorePage;